<template>
  <div :class="gridClass">
    <label v-if="label" class="block text-sm font-medium text-gray-700 mb-1">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <div class="relative">
      <input
        :id="uniqueId"
        type="date"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :min="minDate"
        :max="maxDate"
        @input="handleDateInput"
        @blur="validateDate"
        :class="[
          'w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
          errorMessage ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300',
          customClass
        ]"
      />

    </div>
    
    <!-- Message d'erreur -->
    <p v-if="errorMessage && hasBeenTouched" class="mt-1 text-sm text-red-600">
      {{ errorMessage }}
    </p>
    
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { DateFieldProps } from '@/types/sellerRegister'

const props = withDefaults(defineProps<DateFieldProps>(), {
  required: false,
  disabled: false,
  gridClass: '',
  showValidationInfo: false
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'validation-changed': [isValid: boolean]
}>()

// État local
const errorMessage = ref('')
const hasBeenTouched = ref(false)

// ID unique
const uniqueId = computed(() => `date-${Math.random().toString(36).substr(2, 9)}`)

// Dates min/max calculées
const minDate = computed(() => {
  return props.minDate || undefined
})

const maxDate = computed(() => {
  if (props.maxDate) return props.maxDate
  
  // Par défaut, pas de date maximum (peut être dans le futur)
  return undefined
})

// Validation de la date
const validateDate = () => {
  if (!props.modelValue) {
    if (props.required) {
      emit('validation-changed', false)
      return false
    }
    errorMessage.value = ''
    emit('validation-changed', true)
    return true
  }

  const selectedDate = new Date(props.modelValue)
  
  // Vérifier que c'est une date valide
  if (isNaN(selectedDate.getTime())) {
    errorMessage.value = 'Date invalide'
    emit('validation-changed', false)
    return false
  }
  
  // Vérifier la date minimum
  if (minDate.value) {
    const minDateObj = new Date(minDate.value)
    if (selectedDate < minDateObj) {
      errorMessage.value = `La date doit être après le ${formatDateForDisplay(minDate.value)}`
      emit('validation-changed', false)
      return false
    }
  }
  
  // Vérifier la date maximum
  if (maxDate.value) {
    const maxDateObj = new Date(maxDate.value)
    if (selectedDate > maxDateObj) {
      errorMessage.value = `La date doit être avant ou égale au ${formatDateForDisplay(maxDate.value)}`
      emit('validation-changed', false)
      return false
    }
  }
  
  // Vérifier les dates interdites
  if (props.forbiddenDates && props.forbiddenDates.includes(props.modelValue)) {
    errorMessage.value = 'Cette date n\'est pas autorisée'
    emit('validation-changed', false)
    return false
  }
  
  // Vérifier les jours de la semaine interdits
  if (props.forbiddenWeekDays && props.forbiddenWeekDays.includes(selectedDate.getDay())) {
    const weekDays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
    errorMessage.value = `Les ${weekDays[selectedDate.getDay()]}s ne sont pas autorisés`
    emit('validation-changed', false)
    return false
  }
  
  errorMessage.value = ''
  emit('validation-changed', true)
  return true
}

// Gestion de l'input
const handleDateInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  emit('update:modelValue', value)
  
  if (!hasBeenTouched.value) {
    hasBeenTouched.value = true
  }
}

// Formatage de la date pour l'affichage
const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

// Watcher pour valider automatiquement
watch(() => props.modelValue, () => {
  if (hasBeenTouched.value) {
    validateDate()
  }
})

// Validation initiale si une valeur est déjà présente
watch(() => props.modelValue, (newValue) => {
  if (newValue && !hasBeenTouched.value) {
    validateDate()
  }
}, { immediate: true })
</script>
